import { Table, TableAvailability, TableWeekdayAvailability, TableBookablePeriod } from "@/types/table";
import { format, isWithinInterval, parseISO } from "date-fns";

export function isTableAvailableOnDate(
  table: Table,
  date: Date,
  specificBlockedDates: TableAvailability[],
  weekdayAvailability: TableWeekdayAvailability | null
): boolean {
  // 1. Table désactivée
  if (!table.isActive) return false;
  
  // 2. Date spécifiquement bloquée
  const isBlocked = specificBlockedDates.some(
    (a) => a.tableId === table.id && 
           format(a.date, "yyyy-MM-dd") === format(date, "yyyy-MM-dd")
  );
  if (isBlocked) return false;
  
  // 3. Vérifier le jour de la semaine
  if (weekdayAvailability) {
    const dayOfWeek = date.getDay(); // 0 = dimanche, 6 = samedi
    if (!weekdayAvailability.availableDays.includes(dayOfWeek)) {
      return false;
    }
  }
  
  // 4. Disponible
  return true;
}

/**
 * Vérifie si une table est réservable automatiquement pour une date donnée
 * Prend en compte le flag permanent isBookable ET les périodes temporaires
 */
export function isTableBookableOnDate(
  table: Table,
  date: Date,
  bookablePeriods: TableBookablePeriod[]
): boolean {
  // 1. Si isBookable = false → jamais réservable automatiquement
  if (table.isBookable === false) return false;
  
  // 2. Vérifier les périodes de non-réservabilité temporaires
  const dateStr = format(date, "yyyy-MM-dd");
  const hasBlockingPeriod = bookablePeriods.some(period => {
    if (period.tableId !== table.id) return false;
    const startStr = format(period.startDate, "yyyy-MM-dd");
    const endStr = format(period.endDate, "yyyy-MM-dd");
    return dateStr >= startStr && dateStr <= endStr;
  });
  
  if (hasBlockingPeriod) return false;
  
  // 3. Table réservable automatiquement
  return true;
}

/**
 * Vérifie si une table a une période de non-réservabilité active actuellement
 */
export function hasActiveBookablePeriod(
  tableId: string,
  bookablePeriods: TableBookablePeriod[]
): boolean {
  const today = new Date();
  const todayStr = format(today, "yyyy-MM-dd");
  
  return bookablePeriods.some(period => {
    if (period.tableId !== tableId) return false;
    const startStr = format(period.startDate, "yyyy-MM-dd");
    const endStr = format(period.endDate, "yyyy-MM-dd");
    return todayStr >= startStr && todayStr <= endStr;
  });
}

export function getAvailableDaysLabel(availableDays: number[]): string {
  const dayNames = ["Dim", "Lun", "Mar", "Mer", "Jeu", "Ven", "Sam"];
  
  if (availableDays.length === 7) return "Tous les jours";
  if (availableDays.length === 0) return "Aucun jour";
  
  const sorted = [...availableDays].sort((a, b) => a - b);
  
  // Détection de plages continues
  if (sorted.length >= 3) {
    const first = sorted[0];
    const last = sorted[sorted.length - 1];
    const isConsecutive = sorted.every((day, i) => 
      i === 0 || day === sorted[i - 1] + 1
    );
    
    if (isConsecutive) {
      return `${dayNames[first]}-${dayNames[last]}`;
    }
  }
  
  return sorted.map(d => dayNames[d]).join(", ");
}

export function getDayName(day: number): string {
  const dayNames = ["Dimanche", "Lundi", "Mardi", "Mercredi", "Jeudi", "Vendredi", "Samedi"];
  return dayNames[day];
}

export function getDayShortName(day: number): string {
  const dayNames = ["Dim", "Lun", "Mar", "Mer", "Jeu", "Ven", "Sam"];
  return dayNames[day];
}

/**
 * Vérifie uniquement si une table est en période de non-réservabilité temporaire.
 * Utilisée pour les composantes de tables combinées, où le flag is_bookable
 * de la combinée fait foi (pas celui des composantes individuelles).
 */
export function isTableInTemporaryBlockingPeriod(
  tableId: string,
  date: Date,
  bookablePeriods: TableBookablePeriod[]
): boolean {
  const dateStr = format(date, "yyyy-MM-dd");
  return bookablePeriods.some(period => {
    if (period.tableId !== tableId) return false;
    const startStr = format(period.startDate, "yyyy-MM-dd");
    const endStr = format(period.endDate, "yyyy-MM-dd");
    return dateStr >= startStr && dateStr <= endStr;
  });
}
